import {
	type PointerEvent as ReactPointerEvent,
	type RefObject,
	useEffect,
	useRef,
	useState,
} from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';
import { Tab } from '@/libraries/design/patterns/tabs/wac-refresh-tabs/src/tab';
import { TabList } from '@/libraries/design/patterns/tabs/wac-refresh-tabs/src/tab-list';
import { TabPanel } from '@/libraries/design/patterns/tabs/wac-refresh-tabs/src/tab-panel';

import { JsmPlaygroundCursor, type CursorColor } from './components/jsm-playground-cursor';
import { JsmPlaygroundNavigation } from './components/jsm-playground-navigation';
import { JsmPlaygroundItOperations } from './jsm-playground-it-operations';
import { teamNavigationConfig, teamTabs, type TeamTabValue } from './jsm-portal-playground-data';
import { jsmPortalPlaygroundMessages as messages } from './jsm-portal-playground-messages';

/**
 * The preview's designed box — the width of the WAC content column at desktop, and the height the
 * card is pinned to.
 *
 * Narrower breakpoints do not get their own preview. Figma draws this same card at 933×565
 * (tablet) and 722×436 (mobile), both the desktop card's 1.56 aspect ratio scaled to the content
 * column. So the card always renders at its designed size and a transform fits it to whatever
 * width is actually available: everything inside keeps its desktop CSS box, which is what lets the
 * real interactive preview carry down to narrow widths untouched rather than reflowing into a
 * different layout.
 */
const PREVIEW_DESIGN_WIDTH = 1168;
const PREVIEW_DESIGN_HEIGHT = 750;

/**
 * Measures the available column and returns the factor that fits the designed card into it. Never
 * scales past 1 — above the 1168px cap the card is already at its designed size and the extra room
 * stays as page margin.
 */
const usePreviewScale = (): [RefObject<HTMLDivElement>, number] => {
	const ref = useRef<HTMLDivElement>(null);
	const [scale, setScale] = useState(1);

	useEffect(() => {
		const element = ref.current;
		if (!element) {
			return;
		}

		const measure = () => {
			const width = element.clientWidth;
			// Guard the zero-width first frame: scaling to 0 collapses the card and, with it, the
			// observed box, so the observer would have nothing left to re-measure.
			if (width > 0) {
				setScale(Math.min(1, width / PREVIEW_DESIGN_WIDTH));
			}
		};

		const observer = new ResizeObserver(measure);
		observer.observe(element);
		measure();

		return () => observer.disconnect();
	}, []);

	return [ref, scale];
};

export type UniformJsmPortalPlaygroundProps = {
	initialActiveTab?: TeamTabValue;
	cursorLabel?: string;
	cursorColor?: CursorColor;
};

export const UniformJsmPortalPlayground = ({
	initialActiveTab = 'it-support',
	cursorLabel,
	cursorColor = 'yellow',
}: UniformJsmPortalPlaygroundProps = {}) => {
	const intl = useIntl();
	const [activeTab, setActiveTab] = useState<TeamTabValue>(initialActiveTab);
	const [isCursorActive, setIsCursorActive] = useState(false);
	const [isCursorHovering, setIsCursorHovering] = useState(false);
	const [isCursorClicking, setIsCursorClicking] = useState(false);
	const [hasUserTakenOver, setHasUserTakenOver] = useState(false);
	const cursorRef = useRef<HTMLDivElement>(null);
	const isPointerInsidePreviewRef = useRef(false);
	const isHoveringInteractiveRef = useRef(false);
	const [previewColumnRef, previewScale] = usePreviewScale();

	const updateCursorPosition = (event: ReactPointerEvent<HTMLElement>) => {
		const cursor = cursorRef.current;

		if (!cursor) {
			return;
		}

		const sectionBounds = event.currentTarget.getBoundingClientRect();
		cursor.style.setProperty('--cursor-x', `${event.clientX - sectionBounds.left}px`);
		cursor.style.setProperty('--cursor-y', `${event.clientY - sectionBounds.top}px`);

		const isClickable = isClickableElement(event.target);
		if (isClickable !== isHoveringInteractiveRef.current) {
			isHoveringInteractiveRef.current = isClickable;
			setIsCursorHovering(isClickable);
		}
	};

	const handlePreviewPointerEnter = (event: ReactPointerEvent<HTMLDivElement>) => {
		isPointerInsidePreviewRef.current = true;
		updateCursorPosition(event);
		setIsCursorActive(true);
	};

	const handlePreviewPointerLeave = () => {
		isPointerInsidePreviewRef.current = false;
		isHoveringInteractiveRef.current = false;
		setIsCursorActive(false);
		setIsCursorHovering(false);
		setIsCursorClicking(false);
	};

	const handlePreviewFocusChange = () => {
		if (!isPointerInsidePreviewRef.current) {
			isHoveringInteractiveRef.current = false;
			setIsCursorActive(false);
			setIsCursorHovering(false);
			setIsCursorClicking(false);
		}
	};

	const handlePreviewPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
		setHasUserTakenOver(true);
		if (isClickableElement(event.target)) {
			setIsCursorClicking(true);
		}
	};

	const handlePreviewPointerUp = () => {
		setIsCursorClicking(false);
	};

	const handleTabSelect = (id: string) => {
		setHasUserTakenOver(false);
		setActiveTab(id as TeamTabValue);
	};

	return (
		<section css={playgroundSectionStyles} aria-label={intl.formatMessage(messages.sectionLabel)}>
			<div css={tabListWrapperStyles}>
				<TabList
					activeTabId={activeTab}
					aria-label={intl.formatMessage(messages.serviceTeamTabListLabel)}
				>
					{teamTabs.map((teamTab, index) => (
						<Tab
							key={teamTab.value}
							tabId={teamTab.value}
							index={index}
							title={intl.formatMessage(teamTab.label)}
							isActive={teamTab.value === activeTab}
							onTabSelect={handleTabSelect}
							tabSize="small"
						/>
					))}
				</TabList>
			</div>

			<div css={previewOuterStyles} ref={previewColumnRef}>
				{/* Deliberately outside the scaled frame: the cursor tracks the pointer in rendered
				 * pixels, so scaling it too would shrink the pill and double-apply the factor. */}
				<JsmPlaygroundCursor
					ref={cursorRef}
					label={cursorLabel ?? intl.formatMessage(messages.customCursorLabel)}
					color={cursorColor}
					isActive={isCursorActive}
					isHovering={isCursorHovering}
					isClicking={isCursorClicking}
				/>
				{/* The outer div reserves the card's *rendered* height — a transform is paint-time and
				 * does not affect layout, so without this the page below would overlap the card at any
				 * scale under 1. The inner div is the designed box, scaled about its top-left so the
				 * card's leading edge stays put rather than drifting inward as it shrinks. */}
				<div
					css={previewScaleFrameStyles}
					style={{ height: `${PREVIEW_DESIGN_HEIGHT * previewScale}px` }}
				>
					<div
						css={previewScaleInnerStyles}
						data-preview-scale={previewScale}
						style={{ transform: `scale(${previewScale})` }}
					>
						<div
							css={previewCardStyles}
							onBlur={handlePreviewFocusChange}
							onFocus={handlePreviewFocusChange}
							onPointerDown={handlePreviewPointerDown}
							onPointerEnter={handlePreviewPointerEnter}
							onPointerLeave={handlePreviewPointerLeave}
							onPointerMove={updateCursorPosition}
							onPointerUp={handlePreviewPointerUp}
						>
							<div css={panelStackStyles}>
								{teamTabs.map((teamTab) => (
									<TabPanel
										key={teamTab.value}
										id={teamTab.value}
										isActive={teamTab.value === activeTab}
									>
										<div css={teamPreviewStyles}>
											<JsmPlaygroundNavigation
												config={teamNavigationConfig[teamTab.value]}
												teamName={intl.formatMessage(teamTab.label)}
											/>
											{teamTab.value === 'it-operations' && activeTab === 'it-operations' ? (
												<JsmPlaygroundItOperations hasUserTakenOver={hasUserTakenOver} />
											) : (
												<div css={blankPreviewStyles} />
											)}
										</div>
									</TabPanel>
								))}
							</div>
						</div>
					</div>
				</div>
			</div>
		</section>
	);
};

export default UniformJsmPortalPlayground;

const playgroundSectionStyles = css({
	position: 'relative',
	boxSizing: 'border-box',
	width: '100%',
	overflowX: 'clip',
	paddingBlockStart: token('space.400'),
	paddingBlockEnd: token('space.600'),
	paddingInline: token('space.300'),
	backgroundColor: token('elevation.surface'),
	[media.above.md]: {
		paddingInline: token('space.600'),
	},
});

const tabListWrapperStyles = css({
	display: 'flex',
	justifyContent: 'center',
	width: 'fit-content',
	maxWidth: '100%',
	marginInline: 'auto',
	marginBlockEnd: token('space.400'),
	// Override the shared TabList background and border for the playground's outlined theme,
	// keeping styling changes scoped to this component without modifying the shared design pattern.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'> div': {
		backgroundColor: `${wacToken('color.neutral.light0')} !important`,
		border: `1px solid ${wacToken('color.primary.black')} !important`,
		[media.below.sm]: {
			backgroundColor: 'transparent !important',
			border: 'none !important',
		},
	},
	// Keep tab horizontal padding compact (20px) so the switcher is ~480px wide matching the prototype
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'a[role="tab"]': {
		paddingInline: `${token('space.250')} !important`,
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: token('font.weight.semibold'),
	},
});

// `minmax(0, 1fr)` rather than `1fr`: a bare `1fr` track has an automatic minimum of its
// content's size, so a preview taller than the card (the help center runs well past the fold)
// would stretch the row instead of being clipped by it.
const panelStackStyles = css({
	display: 'grid',
	gridTemplateColumns: 'minmax(0, 1fr)',
	gridTemplateRows: 'minmax(0, 1fr)',
	width: '100%',
	height: '100%',
	minHeight: 0,
});

const previewOuterStyles = css({
	position: 'relative',
	width: '100%',
	maxWidth: `${PREVIEW_DESIGN_WIDTH}px`,
	marginInline: 'auto',
	boxSizing: 'border-box',
});

// Reserves the card's rendered height; the transform itself contributes nothing to layout.
const previewScaleFrameStyles = css({
	position: 'relative',
	width: '100%',
});

const previewScaleInnerStyles = css({
	width: `${PREVIEW_DESIGN_WIDTH}px`,
	height: `${PREVIEW_DESIGN_HEIGHT}px`,
	transformOrigin: 'top left',
});

const previewCardStyles = css({
	position: 'relative',
	zIndex: 1,
	boxSizing: 'border-box',
	width: '100%',
	height: `${PREVIEW_DESIGN_HEIGHT}px`,
	border: `${token('border.width')} solid ${token('color.border')}`,
	borderRadius: token('radius.xlarge'),
	backgroundColor: token('elevation.surface'),
	boxShadow: token('elevation.shadow.overlay'),
	// Clips the preview to the card's fixed 750px stage. Safe for the shadow: an element's own
	// `overflow` never clips its own `box-shadow` — only an intermediate ancestor's would.
	overflow: 'hidden',
	cursor: 'none',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	'*': {
		cursor: 'none !important',
	},
	'@media (pointer: coarse)': {
		cursor: 'auto',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		'*': {
			cursor: 'auto !important',
		},
	},
});

const blankPreviewStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	flex: '1 1 auto',
	width: '100%',
	borderRadius: token('radius.xlarge'),
	backgroundColor: token('elevation.surface'),
});

const teamPreviewStyles = css({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	height: '100%',
	borderRadius: token('radius.xlarge'),
	overflow: 'hidden',
});

const isClickableElement = (target: EventTarget | null): boolean => {
	if (!(target instanceof HTMLElement)) {
		return false;
	}

	const clickableSelector = [
		'button',
		'a[href]',
		'input:not([disabled])',
		'select:not([disabled])',
		'textarea:not([disabled])',
		'label',
		'[role="button"]',
		'[role="checkbox"]',
		'[role="tab"]',
		'[role="link"]',
		'[data-clickable="true"]',
	].join(', ');

	return Boolean(target.closest(clickableSelector));
};
